import { createContext, useContext, useReducer, useMemo, ReactNode, Dispatch } from "react";

// The Argon Dashboard 2 MUI main context
interface ArgonController {
  miniSidenav: boolean;
  darkSidenav: boolean;
  sidenavColor: string | null;
  transparentNavbar: boolean;
  fixedNavbar: boolean;
  openConfigurator: boolean;
  direction: "ltr" | "rtl";
  layout: string;
  darkMode: boolean;
}

type ArgonAction =
  | { type: "MINI_SIDENAV"; value: boolean }
  | { type: "DARK_SIDENAV"; value: boolean }
  | { type: "SIDENAV_COLOR"; value: string | null }
  | { type: "TRANSPARENT_NAVBAR"; value: boolean }
  | { type: "FIXED_NAVBAR"; value: boolean }
  | { type: "OPEN_CONFIGURATOR"; value: boolean }
  | { type: "DIRECTION"; value: "ltr" | "rtl" }
  | { type: "LAYOUT"; value: string }
  | { type: "DARK_MODE"; value: boolean };

const Argon = createContext<[ArgonController, Dispatch<ArgonAction>] | null>(null);

// Setting custom name for the context which is visible on react dev tools
if (Argon) {
  (Argon as any).displayName = "ArgonContext";
}

// Argon Dashboard 2 MUI reducer
function reducer(state: ArgonController, action: ArgonAction): ArgonController {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "DARK_SIDENAV": {
      return { ...state, darkSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARK_MODE": {
      return { ...state, darkMode: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${(action as any).type}`);
    }
  }
}

// Argon Dashboard 2 MUI context provider
interface ArgonControllerProviderProps {
  children: ReactNode;
}

function ArgonControllerProvider({ children }: ArgonControllerProviderProps): React.JSX.Element {
  const initialState: ArgonController = {
    miniSidenav: false,
    darkSidenav: false,
    sidenavColor: null,
    transparentNavbar: true,
    fixedNavbar: false,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo<[ArgonController, Dispatch<ArgonAction>]>(
    () => [controller, dispatch],
    [controller, dispatch]
  );

  return <Argon.Provider value={value}>{children}</Argon.Provider>;
}

// Argon Dashboard 2 MUI custom hook for using context
function useArgonController(): [ArgonController, Dispatch<ArgonAction>] {
  const context = useContext(Argon);

  if (!context) {
    throw new Error("useArgonController should be used inside the ArgonControllerProvider.");
  }

  return context;
}

// Context module functions
const setMiniSidenav = (dispatch: Dispatch<ArgonAction>, value: boolean): void =>
  dispatch({ type: "MINI_SIDENAV", value });
const setDarkSidenav = (dispatch: Dispatch<ArgonAction>, value: boolean): void =>
  dispatch({ type: "DARK_SIDENAV", value });
const setSidenavColor = (dispatch: Dispatch<ArgonAction>, value: string | null): void =>
  dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch: Dispatch<ArgonAction>, value: boolean): void =>
  dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch: Dispatch<ArgonAction>, value: boolean): void =>
  dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch: Dispatch<ArgonAction>, value: boolean): void =>
  dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch: Dispatch<ArgonAction>, value: "ltr" | "rtl"): void =>
  dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch: Dispatch<ArgonAction>, value: string): void =>
  dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch: Dispatch<ArgonAction>, value: boolean): void =>
  dispatch({ type: "DARK_MODE", value });

export {
  ArgonControllerProvider,
  useArgonController,
  setMiniSidenav,
  setDarkSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
};

